/**
 * Clients Management Page
 * Admin page for managing client accounts (parents)
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trash2,
  Mail,
  Phone,
  Users,
  Eye,
  User,
  BookOpen,
  Plus,
  Loader2,
  Pencil,
  UserPlus,
  Download,
} from "lucide-react";
import DataTable from "../../components/admin/DataTable";
import FilterBar from "../../components/admin/FilterBar";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import AdminChildForm from "../../components/admin/AdminChildForm";
import Header from "../../components/Header";
import adminService from "../../api/services/admin.service";
import childrenService from "../../api/services/children.service";
import programsService from "../../api/services/programs.service";
import { formatDate, formatGrade, formatJerseySize } from "../../utils/format";
import { usePermissions } from "../../hooks/usePermissions";
import ClientDetailsPanel from "../../components/admin/ClientDetailsPanel";
import AdminParentForm from "../../components/admin/AdminParentForm";
import toast from "react-hot-toast";

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [programsList, setProgramsList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    action: null,
  });

  // State for expanded client details (children)
  const [expandedClientDetails, setExpandedClientDetails] = useState({});
  const [loadingClientDetails, setLoadingClientDetails] = useState({});

  // State for child form modal
  const [childFormModal, setChildFormModal] = useState({
    isOpen: false,
    mode: "create",
    initialData: null,
    parentId: null,
    parentName: null,
  });

  const { can } = usePermissions();
  const canSeeFinancials = can("canViewFinancials");
  const [stats, setStats] = useState(null);
  const [chip, setChip] = useState("all"); // all | active | attention
  const [detailsPanel, setDetailsPanel] = useState({
    open: false,
    loading: false,
    client: null,
    details: null,
  });

  useEffect(() => {
    adminService
      .getClientStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  const [editingParent, setEditingParent] = useState(false);

  const handleParentSaved = () => {
    setEditingParent(false);
    openDetailsPanel(detailsPanel.client);
    fetchClients();
  };

  const openDetailsPanel = async (client) => {
    setDetailsPanel({ open: true, loading: true, client, details: null });
    try {
      const details = await adminService.getClientById(client.id);
      setDetailsPanel({ open: true, loading: false, client, details });
    } catch (error) {
      console.error("Failed to fetch client details:", error);
      toast.error("Failed to load client details");
      setDetailsPanel({
        open: false,
        loading: false,
        client: null,
        details: null,
      });
    }
  };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;

      const params = {
        skip,
        limit: itemsPerPage,
      };

      if (searchQuery) params.search = searchQuery;
      if (programFilter) params.program_id = programFilter;
      if (statusFilter !== "")
        params.has_active_enrollment = statusFilter === "true";
      if (chip === "active") params.has_active_enrollment = true;
      if (chip === "attention") params.needs_attention = true;

      const response = await adminService.getClients(params);

      // Transform API data
      const clientsData = (response.items || []).map((client) => {
        const nameParts = (client.full_name || "").split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        return {
          id: client.id,
          first_name: firstName,
          last_name: lastName,
          email: client.email || "",
          phone: client.phone || "",
          active_enrollments: client.active_enrollments || 0,
          children_count: client.children_count || 0,
          created_at: client.created_at,
          balance: client.balance,
          last_activity: client.last_activity,
          status: client.status || "inactive",
          needs_attention: client.needs_attention,
        };
      });

      setClients(clientsData);
      setTotalItems(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to load clients");
      setClients([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchQuery, programFilter, chip]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Changing a filter must return to page 1. Without this, narrowing the
  // list while on a later page asks the API to skip past the whole filtered
  // result and the table renders empty.
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, programFilter]);

  // Fetch programs for filter dropdown
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const data = await programsService.getAll();
        const list = Array.isArray(data) ? data : data.items || data.data || [];
        setProgramsList(list);
      } catch (error) {
        console.error("Failed to fetch programs:", error);
      }
    };
    loadPrograms();
  }, []);

  // View client = expand the row to show children
  const handleViewClient = (toggleExpand) => {
    if (toggleExpand) toggleExpand();
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await adminService.deleteClient(clientId);
      toast.success("Client deleted successfully");
      fetchClients();
      setConfirmDialog({ isOpen: false });
    } catch (error) {
      console.error("Failed to delete client:", error);
      const errorMessage = error.message || "Failed to delete client";
      toast.error(errorMessage);
    }
  };

  // Delete a child (with optional force for admin)
  const handleDeleteChild = async (childId, clientId, force = false) => {
    try {
      await childrenService.delete(childId, force);
      toast.success("Child deleted successfully");
      // Refresh the expanded client details
      setExpandedClientDetails((prev) => {
        const updated = { ...prev };
        delete updated[clientId];
        return updated;
      });
      // Re-fetch client details
      handleExpandClient({ id: clientId });
      fetchClients(); // Refresh counts
      setConfirmDialog({ isOpen: false });
    } catch (error) {
      console.error("Failed to delete child:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to delete child",
      );
    }
  };

  // Pre-check before deleting a child (admin)
  const handleDeleteChildClick = async (child, clientId) => {
    try {
      const check = await childrenService.deleteCheck(child.id);
      if (check.can_delete) {
        // No blocking factors - show simple confirm
        setConfirmDialog({
          isOpen: true,
          title: "Delete Child",
          message: `Are you sure you want to delete "${child.first_name} ${child.last_name}"? This action cannot be undone.`,
          action: () => handleDeleteChild(child.id, clientId),
        });
      } else {
        // Has active enrollments - show force delete option
        const enrollmentList = (check.active_enrollments || [])
          .map((e) => `• ${e.class_name} (${e.status})`)
          .join("\n");
        const subNote = check.has_stripe_subscriptions
          ? "\n\nActive Stripe subscriptions will be cancelled immediately."
          : "";
        setConfirmDialog({
          isOpen: true,
          title: "Force Delete Child",
          message: `"${child.first_name} ${child.last_name}" has active enrollments:\n\n${enrollmentList}${subNote}\n\nForce delete will cancel all enrollments and subscriptions. Continue?`,
          action: () => handleDeleteChild(child.id, clientId, true),
        });
      }
    } catch (error) {
      console.error("Failed to check delete:", error);
      // Fallback to simple confirm
      setConfirmDialog({
        isOpen: true,
        title: "Delete Child",
        message: `Are you sure you want to delete "${child.first_name} ${child.last_name}"? This action cannot be undone.`,
        action: () => handleDeleteChild(child.id, clientId),
      });
    }
  };

  // Refresh child data after create/edit
  const handleChildFormSuccess = (clientId) => {
    handleExpandClient({ id: clientId }, true);
    fetchClients(); // Refresh counts
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Handle row expansion - fetch client details including children
  const handleExpandClient = async (client, forceRefresh = false) => {
    // Skip if already loaded (unless force refresh)
    if (!forceRefresh && expandedClientDetails[client.id]) return;

    setLoadingClientDetails((prev) => ({ ...prev, [client.id]: true }));

    try {
      const details = await adminService.getClientById(client.id);
      setExpandedClientDetails((prev) => ({
        ...prev,
        [client.id]: details,
      }));
    } catch (error) {
      console.error("Failed to fetch client details:", error);
      toast.error("Failed to load client details");
    } finally {
      setLoadingClientDetails((prev) => ({ ...prev, [client.id]: false }));
    }
  };

  // Render expanded row content - show children details
  const renderExpandedContent = (client) => {
    const isLoading = loadingClientDetails[client.id];
    const details = expandedClientDetails[client.id];

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin text-btn-gold mr-2" />
          <span className="text-sm text-text-muted font-manrope">
            Loading children details...
          </span>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-center py-6 text-text-muted font-manrope">
          No details available
        </div>
      );
    }

    const children = details.children || [];

    const parentName = `${client.first_name} ${client.last_name}`.trim();

    if (children.length === 0) {
      return (
        <div className="text-center py-6 text-text-muted font-manrope">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>No children registered</p>
          <button
            onClick={() =>
              setChildFormModal({
                isOpen: true,
                mode: "create",
                initialData: null,
                parentId: client.id,
                parentName,
              })
            }
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-btn-gold hover:bg-[#e5ad35] text-text-primary text-xs font-semibold rounded-lg transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Add Child
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-text-primary font-manrope flex items-center gap-2">
            <Users className="w-4 h-4" />
            Registered Children ({children.length})
          </h4>
          <button
            onClick={() =>
              setChildFormModal({
                isOpen: true,
                mode: "create",
                initialData: null,
                parentId: client.id,
                parentName,
              })
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-btn-gold hover:bg-[#e5ad35] text-text-primary text-xs font-semibold rounded-lg transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Add Child
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {children.map((child) => {
            const age = child.date_of_birth
              ? calculateAge(child.date_of_birth)
              : null;
            const enrollments = child.enrollments || [];
            const activeEnrollments = enrollments.filter(
              (e) => e.status === "ACTIVE" || e.status === "active",
            );

            return (
              <div
                key={child.id}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-btn-gold/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-btn-gold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <h5 className="font-semibold text-text-primary font-manrope text-sm truncate">
                        {child.first_name} {child.last_name}
                      </h5>
                      {/* Edit / Delete buttons */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() =>
                            setChildFormModal({
                              isOpen: true,
                              mode: "edit",
                              initialData: child,
                              parentId: client.id,
                              parentName,
                            })
                          }
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Edit child"
                        >
                          <Pencil className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteChildClick(child, client.id)
                          }
                          className="p-1 hover:bg-red-50 rounded transition-colors"
                          title="Delete child"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-text-muted font-manrope">
                      {age && <span>Age: {age}</span>}
                      {child.grade && (
                        <span>• Grade {formatGrade(child.grade)}</span>
                      )}
                      {child.jersey_size && (
                        <span>
                          • Jersey {formatJerseySize(child.jersey_size)}
                        </span>
                      )}
                    </div>

                    {/* Enrollments */}
                    {enrollments.length > 0 ? (
                      <div className="mt-3 space-y-1.5">
                        <p className="text-xs font-medium text-text-muted flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          Enrollments ({activeEnrollments.length} active)
                        </p>
                        <div className="space-y-1">
                          {enrollments.slice(0, 3).map((enrollment, idx) => (
                            <div
                              key={idx}
                              className={`text-xs px-2 py-1 rounded flex items-center justify-between ${
                                enrollment.status === "ACTIVE" ||
                                enrollment.status === "active"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-gray-50 text-gray-600"
                              }`}
                            >
                              <span className="truncate flex-1 mr-2">
                                {enrollment.class?.name ||
                                  enrollment.class_name ||
                                  "Class"}
                              </span>
                              <span className="text-[10px] font-medium uppercase">
                                {enrollment.status}
                              </span>
                            </div>
                          ))}
                          {enrollments.length > 3 && (
                            <p className="text-xs text-text-muted italic pl-2">
                              +{enrollments.length - 3} more
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-text-muted italic">
                        No enrollments
                      </p>
                    )}

                    {/* Emergency Contacts */}
                    <div className="mt-3 space-y-1.5">
                      <p className="text-xs font-medium text-text-muted flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        Emergency Contacts
                      </p>
                      {child.emergency_contacts &&
                      child.emergency_contacts.length > 0 ? (
                        <div className="space-y-1">
                          {child.emergency_contacts.map((contact, idx) => (
                            <div
                              key={idx}
                              className="text-xs px-2 py-1 rounded bg-gray-50 text-gray-600"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate font-medium text-text-primary">
                                  {contact.name || "—"}
                                </span>
                                {(contact.relation || contact.relationship) && (
                                  <span className="text-[10px] uppercase shrink-0">
                                    {contact.relation || contact.relationship}
                                  </span>
                                )}
                              </div>
                              {contact.phone && (
                                <span className="text-text-muted">
                                  {contact.phone}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-text-muted italic">
                          No emergency contacts
                        </p>
                      )}
                    </div>

                    {/* Add Class Button */}
                    <button
                      onClick={() => navigate(`/class?child_id=${child.id}`)}
                      className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-btn-gold hover:bg-[#e5ad35] text-text-primary text-xs font-semibold rounded-lg transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Class
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper to calculate age
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const columns = [
    {
      key: "name",
      label: "Client",
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#173151] flex items-center justify-center text-white font-semibold text-sm">
            {row.first_name?.[0]?.toUpperCase()}
            {row.last_name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="font-semibold font-manrope text-text-primary">
              {row.first_name} {row.last_name}
            </p>
            <div className="flex items-center gap-1 text-xs text-text-muted">
              <Mail className="w-3 h-3" />
              <button
                type="button"
                title="Compose email to this user"
                className="hover:underline hover:text-btn-secondary text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/admin/mass-email", {
                    state: {
                      recipient: {
                        id: row.id,
                        email: row.email,
                        name: `${row.first_name || ""} ${row.last_name || ""}`.trim(),
                      },
                    },
                  });
                }}
              >
                {row.email}
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "phone",
      label: "Phone",
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1 text-sm font-manrope text-text-muted">
          {value ? (
            <>
              <Phone className="w-4 h-4" />
              <span>{value}</span>
            </>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </div>
      ),
    },
    {
      key: "children_count",
      label: "Children",
      sortable: true,
      render: (value, row, { toggleExpand, isExpanded, expandable } = {}) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (expandable && toggleExpand) toggleExpand();
          }}
          className={`flex items-center gap-1.5 text-sm font-manrope px-2 py-1 rounded-lg transition-colors ${
            isExpanded
              ? "bg-btn-gold/20 text-btn-gold"
              : "text-text-muted hover:bg-gray-100 hover:text-text-primary"
          } ${expandable ? "cursor-pointer" : ""}`}
          type="button"
        >
          <Users className="w-4 h-4" />
          <span className="font-medium">{value || 0}</span>
          {value > 0 && expandable && (
            <span className="text-xs opacity-60">{isExpanded ? "▲" : "▼"}</span>
          )}
        </button>
      ),
    },
    {
      key: "active_enrollments",
      label: "Enrollments",
      sortable: true,
      render: (value) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            value > 0
              ? "bg-[#DFF5E8] text-status-success"
              : "bg-neutral-lightest text-neutral-dark"
          }`}
        >
          {value || 0} {value === 1 ? "Class" : "Classes"}
        </span>
      ),
    },
    ...(canSeeFinancials
      ? [
          {
            key: "balance",
            label: "Balance",
            sortable: true,
            render: (value) =>
              value > 0 ? (
                <span className="font-semibold text-red-600 font-manrope">
                  ${Number(value).toFixed(2)}
                </span>
              ) : (
                <span className="text-gray-500 font-manrope">$0.00</span>
              ),
          },
        ]
      : []),
    {
      key: "last_activity",
      label: "Last Activity",
      sortable: true,
      hideOnMobile: true,
      render: (value) => (
        <span className="text-sm text-gray-600 font-manrope">
          {value ? formatDate(value) : "—"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (value) => {
        const styles = {
          active: "bg-green-100 text-green-800",
          payment_due: "bg-red-100 text-red-700",
          inactive: "bg-gray-100 text-gray-600",
        };
        const labels = {
          active: "Active",
          payment_due: "Payment due",
          inactive: "Inactive",
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${
              styles[value] || styles.inactive
            }`}
          >
            {labels[value] || value}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Joined",
      sortable: true,
      render: (value) => (
        <span className="text-sm font-manrope text-text-muted">
          {formatDate(value)}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      type: "actions",
      align: "right",
      actions: (row, { toggleExpand } = {}) => [
        {
          label: "View",
          icon: Eye,
          onClick: () => openDetailsPanel(row),
        },
        {
          label: "Delete",
          icon: Trash2,
          variant: "destructive",
          onClick: () => {
            setConfirmDialog({
              isOpen: true,
              title: "Delete Client",
              message: `Are you sure you want to delete "${row.first_name} ${row.last_name}"? This action cannot be undone.`,
              action: () => handleDeleteClient(row.id),
            });
          },
        },
      ],
    },
  ];

  const filters = [
    {
      type: "select",
      placeholder: "All Programs",
      value: programFilter,
      onChange: setProgramFilter,
      options: [
        { value: "", label: "All Programs" },
        ...programsList.map((p) => ({ value: p.id, label: p.name })),
      ],
    },
    {
      type: "select",
      placeholder: "All Clients",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: "", label: "All Clients" },
        { value: "true", label: "With Enrollments" },
        { value: "false", label: "No Enrollments" },
      ],
    },
  ];

  const hasActiveFilters = statusFilter || searchQuery || programFilter;
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setProgramFilter("");
    setCurrentPage(1);
  };

  const handleExportStudents = async () => {
    setExporting(true);
    try {
      const blob = await adminService.exportStudentsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `students_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Students CSV exported");
    } catch (err) {
      console.error("Failed to export students CSV:", err);
      toast.error("Failed to export students CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-9xl mx-auto px-3 sm:px-4 py-4 flex-1 flex flex-col min-h-0 w-full">
        <div className="shrink-0 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center items-start gap-3 sm:gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif truncate">
              Clients Management
            </h1>
            <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1 hidden sm:block">
              Manage client accounts and their enrollments
            </p>
          </div>
          <button
            onClick={handleExportStudents}
            disabled={exporting}
            title="Download all parent + child fields (incl. emergency contacts) as CSV"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-btn-gold hover:bg-[#e5ad35] disabled:opacity-60 text-text-primary text-xs sm:text-sm font-semibold rounded-lg transition-colors shrink-0"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>

        {stats && (
          <div className="shrink-0 flex flex-wrap gap-2 mb-3">
            {[
              { key: "all", label: "All Clients", count: stats.total },
              {
                key: "active",
                label: "Active",
                count: stats.active,
                dot: "bg-green-500",
              },
              ...(stats.needs_attention != null
                ? [
                    {
                      key: "attention",
                      label: "Needs Attention",
                      count: stats.needs_attention,
                      dot: "bg-red-500",
                    },
                  ]
                : []),
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  setChip(c.key);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold font-manrope border transition-colors ${
                  chip === c.key
                    ? "bg-[#173151] text-white border-[#173151]"
                    : "bg-white/60 text-[#173151] border-white/40 hover:border-[#F3BC48]"
                }`}
              >
                {c.dot && <span className={`w-2 h-2 rounded-full ${c.dot}`} />}
                {c.label}
                <span
                  className={`px-1.5 py-0.5 rounded-md text-xs ${
                    chip === c.key ? "bg-white/20" : "bg-[#173151]/10"
                  }`}
                >
                  {c.count}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="shrink-0">
          <FilterBar
            searchValue={searchQuery}
            searchPlaceholder="Search by name or email..."
            onSearch={setSearchQuery}
            filters={filters}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
          />
        </div>

        <div className="flex-1 min-h-0 flex flex-col pb-2">
          <DataTable
            columns={columns}
            data={clients}
            loading={loading}
            emptyMessage="No clients found"
            pagination={true}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            expandable={true}
            onExpand={handleExpandClient}
            renderExpanded={renderExpandedContent}
          />
        </div>
      </div>

      <ClientDetailsPanel
        open={detailsPanel.open}
        loading={detailsPanel.loading}
        client={detailsPanel.client}
        details={detailsPanel.details}
        canSeeFinancials={canSeeFinancials}
        onClose={() =>
          setDetailsPanel({
            open: false,
            loading: false,
            client: null,
            details: null,
          })
        }
        onSendMessage={() => {
          const c = detailsPanel.client;
          navigate("/admin/mass-email", {
            state: {
              recipient: {
                id: c.id,
                email: c.email,
                name: `${c.first_name} ${c.last_name}`.trim(),
              },
            },
          });
        }}
        onViewInvoices={() => navigate("/admin/invoices?status=open")}
        onEdit={() => setEditingParent(true)}
      />

      <AdminParentForm
        isOpen={editingParent}
        parent={detailsPanel.details}
        onClose={() => setEditingParent(false)}
        onSaved={handleParentSaved}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant="danger"
      />

      <AdminChildForm
        isOpen={childFormModal.isOpen}
        onClose={() =>
          setChildFormModal((prev) => ({ ...prev, isOpen: false }))
        }
        mode={childFormModal.mode}
        initialData={childFormModal.initialData}
        parentId={childFormModal.parentId}
        parentName={childFormModal.parentName}
        onSuccess={() => handleChildFormSuccess(childFormModal.parentId)}
      />
    </div>
  );
}
